import { useCallback, useEffect, useMemo, useRef } from 'react';
import deepEquals from 'lodash/isEqual';

import {
	useLoadingStatusService,
	useService,
	type LoadingStatus,
} from '@atlassian/jira-common-services/src/use-service';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	CUSTOM_FIELD_MODULE,
	CUSTOM_FIELD_TYPE_MODULE,
	SOURCE_GLOBAL_ISSUE_CREATE,
	UI_MODIFICATIONS_MODULE,
} from '@atlassian/jira-forge-ui-constants';
import type {
	IssueAdjustmentExtension,
	UiModificationsExtension,
} from '@atlassian/jira-forge-ui-types';
import { doesExtensionMeetConditions } from '@atlassian/jira-forge-ui-utils/src/utils/conditions';

import fetchForgeModulesV3 from '@atlassian/jira-forge-ui-utils/src/utils/fetch-modules-v3';

import type { CustomFieldExtension, ForgeContext } from '@atlassian/jira-issue-create-common-types';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';

type Data = {
	extensions: {
		jiraCustomField: CustomFieldExtension[];
		jiraCustomFieldType: CustomFieldExtension[];
		jiraUiModifications: UiModificationsExtension[];
	};
	accessNarrowedExtensions: {
		jiraCustomField: CustomFieldExtension[];
		jiraCustomFieldType: CustomFieldExtension[];
		jiraUiModifications: UiModificationsExtension[];
	};
};

export const useFetchForgeModules = (
	extraContext: ForgeContext = {},
): {
	customFields: CustomFieldExtension[];
	filteredOutModules: CustomFieldExtension[];
	issueAdjustmentModules: IssueAdjustmentExtension[];
	error: Error | undefined;
	/**
	 * loadingStatus will not optional with cleanup of remove-has-finished-loading-in-gic--arken-1245
	 */
	loadingStatus?: LoadingStatus | undefined;
	/**
	 * @deprecated
	 * remove with cleanup remove-has-finished-loading-in-gic--arken-1245
	 */
	hasFinishedLoading: boolean;
} => {
	const isFFRemoveHasFinishedLoadingEnabled = ff('remove-has-finished-loading-in-gic--arken-1245');

	const { cloudId, activationId } = useTenantContext();
	const modulesToFetch = useMemo(
		() => [CUSTOM_FIELD_MODULE, CUSTOM_FIELD_TYPE_MODULE, UI_MODIFICATIONS_MODULE],
		[],
	);

	const classificationTags = extraContext?.classificationTags;
	const previousDataClassificationTags = useRef<string[]>([]);
	const dataClassificationTags = useMemo(() => {
		// When classification tags are not defined, it means the project in GIC is not loaded yet
		if (!classificationTags) {
			return undefined;
		}

		// The `classificationTags` is not referencially stable, so we need to deep compare it and
		// make sure we are only changing this `useMemo` reference when there are values changes
		if (!deepEquals(previousDataClassificationTags.current, classificationTags)) {
			previousDataClassificationTags.current = classificationTags;
		}

		return previousDataClassificationTags.current;
	}, [classificationTags]);
	const hasDataClassificationTags = !!dataClassificationTags;

	const request = useCallback(() => {
		const contextIds = ff('arken-1053-jira-forge-extension-changes-for-aegis_aws9x')
			? [`ari:cloud:jira:${cloudId}:workspace/${activationId}`]
			: undefined;

		return fetchForgeModulesV3(
			cloudId,
			modulesToFetch,
			{ tags: dataClassificationTags || [] },
			{ include: { scopes: true } },
			SOURCE_GLOBAL_ISSUE_CREATE,
			contextIds,
		);
	}, [dataClassificationTags, cloudId, activationId, modulesToFetch]);

	const serviceResultOld = useService<Data>(request, {
		loading: true,
	}); // fetching starts with the fetch() call, so it's fine to have both hooks here
	const serviceResultNew = useLoadingStatusService<Data>(request, {
		loading: true,
	});
	const { data, error, fetch } = isFFRemoveHasFinishedLoadingEnabled
		? serviceResultNew
		: serviceResultOld;

	useEffect(() => {
		// Since access narrowing, we need to know if the selected project in GIC has or not data
		// classification tags in it, and make sure we call cs-extensions with those.
		//
		// So if data classification tags are not yet loaded, the request should not be invoked
		if (!hasDataClassificationTags) return;

		fetch();
	}, [fetch, hasDataClassificationTags]);

	return useMemo(() => {
		const customFields: Array<CustomFieldExtension> = [];
		const issueAdjustmentModules: Array<IssueAdjustmentExtension> = [];
		const filteredOutModules: Array<CustomFieldExtension> = [];

		const modules = data?.extensions;
		const accessNarrowedModules = data?.accessNarrowedExtensions;

		accessNarrowedModules?.jiraCustomField?.forEach((module: CustomFieldExtension) => {
			filteredOutModules.push(module);
		});
		accessNarrowedModules?.jiraCustomFieldType?.forEach((module: CustomFieldExtension) => {
			filteredOutModules.push(module);
		});

		modules?.jiraCustomField?.forEach((module: CustomFieldExtension) => {
			if (doesExtensionMeetConditions(module, extraContext)) {
				customFields.push(module);
			} else {
				filteredOutModules.push(module);
			}
		});

		modules?.jiraCustomFieldType?.forEach((module: CustomFieldExtension) => {
			if (doesExtensionMeetConditions(module, extraContext)) {
				customFields.push(module);
			} else {
				filteredOutModules.push(module);
			}
		});

		modules?.jiraUiModifications?.forEach((module: UiModificationsExtension) => {
			if (doesExtensionMeetConditions(module, extraContext)) {
				issueAdjustmentModules.push(module);
			}
		});

		if (isFFRemoveHasFinishedLoadingEnabled) {
			const loadingStatus = serviceResultNew.loadingStatus; // cleanup remove-has-finished-loading-in-gic--arken-1245: destructure loading from useLoadingStatusService directly

			return {
				customFields,
				issueAdjustmentModules,
				filteredOutModules,
				error,
				loadingStatus,
				// cleanup remove-has-finished-loading-in-gic--arken-1245: remove hasFinishedLoading: this is not needed but it is dangerous to have undefined here as it is also falsey
				hasFinishedLoading: loadingStatus === 'success' || loadingStatus === 'error',
			};
		}

		const loading = serviceResultOld.loading;
		const hasFinishedLoading = !loading && (data !== undefined || error !== undefined);

		return {
			customFields,
			issueAdjustmentModules,
			filteredOutModules,
			error,
			hasFinishedLoading,
		};
	}, [
		data,
		isFFRemoveHasFinishedLoadingEnabled,
		serviceResultNew.loadingStatus,
		serviceResultOld.loading,
		error,
		extraContext,
	]);
};
